import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BrochureFileList } from '@/components/BrochureFileList';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/utils/api';
import { useCurrency, Usd, PriceCaption } from '@/utils/CurrencyContext';
import { isAuthenticated, getUser } from '@/utils/auth';
import { toast } from 'sonner';
import { BookOpen, Users, Play, CheckCircle, Video, Calendar, ArrowLeft, Loader2, ClipboardList, Award, XCircle, Clock, AlertTriangle, FileText, Image, FileImage, Lock } from 'lucide-react';
import { AuthModal } from '@/components/AuthModal';
import { motion } from 'framer-motion';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [courseData, setCourseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ paypal: true });
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [lessonQuizzes, setLessonQuizzes] = useState({});
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [isSubmittingQuiz, setIsSubmittingQuiz] = useState(false);
  
  // Course terms acceptance state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [courseTerms, setCourseTerms] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  
  // Declaration acceptance state
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [courseDeclaration, setCourseDeclaration] = useState(null);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [isAcceptingDeclaration, setIsAcceptingDeclaration] = useState(false);
  const [hasAcceptedDeclaration, setHasAcceptedDeclaration] = useState(false);
  
  // Certificate/Completion state
  const [completionStatus, setCompletionStatus] = useState(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);

  const downloadCertificate = async () => {
    setDownloadingCertificate(true);
    try {
      const res = await api.get(`/certificates/${courseId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Certificate_of_Completion.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Your certificate has not been unlocked yet' : 'Failed to download certificate');
    } finally {
      setDownloadingCertificate(false);
    }
  };
  
  // Tabs state for Course Content/Brochure/Gallery
  const [activeContentTab, setActiveContentTab] = useState('content');
  // Per-course gallery state
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    fetchCourseDetail();
    fetchPaymentMethods();
  }, [courseId]);

  // Fetch per-course gallery images
  useEffect(() => {
    if (courseId) {
      fetchGalleryImages();
    }
  }, [courseId]);

  // Check terms acceptance when enrolled
  useEffect(() => {
    if (courseData?.is_enrolled && isAuthenticated()) {
      checkTermsAcceptance();
      fetchCompletionStatus();
    }
  }, [courseData?.is_enrolled]);

  // Fetch completion status for enrolled students
  const fetchCompletionStatus = async () => {
    if (!isAuthenticated()) return;
    const user = getUser();
    if (user?.role !== 'student') return;
    
    try {
      const response = await api.get(`/courses/${courseId}/student-completion-status`);
      setCompletionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch completion status:', error);
    }
  };

  // Fetch active declaration for the course
  useEffect(() => {
    if (courseId) {
      fetchActiveDeclaration();
    }
  }, [courseId]);

  const fetchActiveDeclaration = async () => {
    try {
      const response = await api.get(`/courses/${courseId}/declaration/active`);
      if (response.data.declaration) {
        setCourseDeclaration(response.data.declaration);
      } else {
        setCourseDeclaration(null);
      }
    } catch (error) {
      console.error('Error fetching declaration:', error);
      setCourseDeclaration(null);
    }
  };

  const checkTermsAcceptance = async () => {
    try {
      const response = await api.get(`/courses/${courseId}/terms/acceptance-status`);
      if (response.data.requires_acceptance) {
        setCourseTerms({
          terms_and_conditions: response.data.terms_and_conditions,
          terms_updated_at: response.data.terms_updated_at,
          course_title: response.data.course_title,
          message: response.data.message
        });
        setShowTermsModal(true);
      }
    } catch (error) {
      console.error('Error checking terms acceptance:', error);
    }
  };

  const handleAcceptTerms = async () => {
    setIsAcceptingTerms(true);
    try {
      await api.post(`/courses/${courseId}/terms/accept`);
      setShowTermsModal(false);
      setCourseTerms(null);
      setTermsAccepted(false);
      toast.success('Course terms accepted successfully');
    } catch (error) {
      toast.error('Failed to accept terms');
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  const handleAcceptDeclaration = async () => {
    setIsAcceptingDeclaration(true);
    try {
      await api.post(`/courses/${courseId}/declaration/accept`);
      setShowDeclarationModal(false);
      setDeclarationAccepted(false);
      setHasAcceptedDeclaration(true);
      toast.success('Declaration accepted');
      
      // Now proceed to payment
      if (courseData?.price === 0 || courseData?.price === "0") {
        // Free course - enroll directly
        await enrollFreeCourse();
      } else {
        // Paid course - proceed to PayPal
        handlePayPalPayment();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept declaration');
    } finally {
      setIsAcceptingDeclaration(false);
    }
  };

  const enrollFreeCourse = async () => {
    setIsEnrolling(true);
    try {
      await api.post(`/courses/${courseId}/enroll`);
      toast.success('Successfully enrolled!');
      fetchCourseDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setIsEnrolling(false);
    }
  };

  const fetchCourseDetail = async () => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setCourseData(response.data);
      
      // Fetch quizzes for all lessons if enrolled
      if (response.data.is_enrolled && response.data.lessons?.length > 0) {
        fetchAllLessonQuizzes(response.data.lessons);
      }
    } catch (error) {
      toast.error('Failed to load course details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllLessonQuizzes = async (lessonsList) => {
    const quizzesMap = {};
    for (const lesson of lessonsList) {
      try {
        const response = await api.get(`/lessons/${lesson.id}/quizzes`);
        quizzesMap[lesson.id] = response.data || [];
      } catch (error) {
        quizzesMap[lesson.id] = [];
      }
    }
    setLessonQuizzes(quizzesMap);
  };

  const openQuiz = async (quizId) => {
    try {
      const response = await api.get(`/quizzes/${quizId}`);
      setActiveQuiz(response.data);
      setQuizAnswers({});
      setQuizResult(null);
    } catch (error) {
      toast.error('Failed to load quiz');
    }
  };

  const handleQuizAnswer = (questionIndex, answerIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleSubmitQuiz = async () => {
    if (!activeQuiz) return;
    
    // Check if all questions are answered
    const answeredCount = Object.keys(quizAnswers).length;
    if (answeredCount < activeQuiz.questions.length) {
      toast.error(`Please answer all ${activeQuiz.questions.length} questions`);
      return;
    }

    setIsSubmittingQuiz(true);
    try {
      const response = await api.post(`/quizzes/${activeQuiz.id}/submit`, {
        answers: activeQuiz.questions.map((_, idx) => quizAnswers[idx] || 0)
      });
      setQuizResult(response.data);
      toast.success(`Quiz completed! Score: ${response.data.score}%`);
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setIsSubmittingQuiz(false);
    }
  };

  const closeQuiz = () => {
    setActiveQuiz(null);
    setQuizAnswers({});
    setQuizResult(null);
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/payments/methods');
      setPaymentMethods(response.data);
    } catch (error) {
      // Default to PayPal only
      setPaymentMethods({ paypal: true });
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await api.get(`/courses/${courseId}/gallery`);
      setGalleryImages(response.data || []);
    } catch (error) {
      console.error('Failed to load gallery images:', error);
      setGalleryImages([]);
    }
  };

  const handleEnroll = async () => {
    if (!isAuthenticated()) {
      setShowAuth(true);
      return;
    }

    const user = getUser();
    if (user.role === 'teacher') {
      toast.error('Teachers cannot enroll in courses');
      return;
    }

    // Check if there's an approved declaration that needs to be accepted
    if (courseDeclaration && !hasAcceptedDeclaration) {
      setShowDeclarationModal(true);
      return;
    }

    if (courseData.course.price === 0) {
      // Free course - direct enrollment
      await enrollFreeCourse();
    } else {
      // Paid course - require declaration before proceeding
      if (!courseDeclaration) {
        toast.error('This course requires an approved Acknowledgment Declaration before purchase. Please contact the instructor.');
        return;
      }
      // Paid course with declaration accepted - go to PayPal
      handlePayPalPayment();
    }
  };

  const handlePayPalPayment = async () => {
    setIsPurchasing(true);
    setShowPaymentOptions(false);
    
    try {
      const response = await api.post(`/payments/paypal/create-order?course_id=${courseId}`);
      
      // Use the approval URL from backend (respects PAYPAL_MODE)
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Failed to get PayPal approval URL');
        setIsPurchasing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PayPal initialization failed');
      setIsPurchasing(false);
    }
  };

  const handleAuthSuccess = () => {
    fetchCourseDetail();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#163329' }}></div>
          <p style={{ color: '#5C635A' }}>Loading course...</p>
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Course not found</h2>
          <Button onClick={() => navigate('/courses')}>Browse Courses</Button>
        </div>
      </div>
    );
  }

  const { course, lessons, teacher, is_enrolled, is_expired, days_remaining } = courseData;

  return (
    <>
      <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="course-detail-page">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/courses')}
            className="mb-6"
            data-testid="back-to-courses-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Courses
          </Button>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <h1
                  className="text-4xl md:text-5xl font-bold tracking-tight mb-4"
                  style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
                  data-testid="course-title"
                >
                  {course.title}
                </h1>
                <p className="text-lg mb-6" style={{ color: '#5C635A', lineHeight: '1.7' }}>
                  {course.description}
                </p>

                {/* Course Stats */}
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2" style={{ color: '#5C635A' }}>
                    <BookOpen className="h-5 w-5" />
                    <span>{lessons.length} lessons</span>
                  </div>
                  <div className="flex items-center gap-2" style={{ color: '#5C635A' }}>
                    <Users className="h-5 w-5" />
                    <span>{course.enrollment_count || 0} students</span>
                  </div>
                </div>

                {/* Course Completion Banner - shown when student completes all lessons */}
                {is_enrolled && completionStatus?.all_lessons_completed && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 p-6 rounded-xl"
                    style={{ 
                      background: 'linear-gradient(135deg, #163329 0%, #1e4d3d 100%)',
                      border: '2px solid #4CAF50'
                    }}
                    data-testid="completion-banner"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <Award className="h-12 w-12 text-yellow-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">
                          Congratulations! You've Completed This Course
                        </h3>
                        <p className="text-green-100 mb-4">
                          You have watched all {completionStatus.total_lessons} lessons. 
                          {completionStatus.quiz_score !== null && (
                            <span className="ml-1">
                              Your quiz average is {completionStatus.quiz_score}% 
                              ({completionStatus.passed ? 'Passed' : 'Not Passed'}).
                            </span>
                          )}
                        </p>
                        
                        <div className="flex flex-wrap gap-3">
                          {completionStatus.certificate_unlocked ? (
                            <Button
                              onClick={downloadCertificate}
                              disabled={downloadingCertificate}
                              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                              data-testid="download-certificate-btn"
                            >
                              {downloadingCertificate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Award className="h-4 w-4 mr-2" />}
                              Download Certificate
                            </Button>
                          ) : (
                            <Button
                              onClick={() => {
                                const subject = encodeURIComponent(`Certificate Request - ${course.title}`);
                                const mailtoUrl = `mailto:${completionStatus.instructor_email}?subject=${subject}`;
                                window.location.href = mailtoUrl;
                              }}
                              variant="outline"
                              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                              data-testid="request-certificate-btn"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Request Certificate
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Intro Video */}
                {course.video_intro_url && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      Course Introduction
                    </h2>
                    <div className="video-container rounded-lg overflow-hidden">
                      {course.video_intro_type === 'youtube' ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${new URL(course.video_intro_url).searchParams.get('v')}`}
                          title="Course intro"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <video controls className="w-full">
                          <source src={course.video_intro_url} />
                        </video>
                      )}
                    </div>
                  </div>
                )}

                {/* Course Content / Brochure / Gallery Tabs */}
                <Tabs value={activeContentTab} onValueChange={setActiveContentTab} className="w-full">
                  <div className="flex items-center justify-between mb-4">
                    <TabsList className="grid grid-cols-3 w-auto" style={{ backgroundColor: '#F0EFEA' }}>
                      <TabsTrigger 
                        value="content" 
                        data-testid="content-tab"
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Course Content
                      </TabsTrigger>
                      <TabsTrigger 
                        value="brochure"
                        data-testid="brochure-tab"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Brochure
                      </TabsTrigger>
                      <TabsTrigger 
                        value="gallery"
                        data-testid="gallery-tab"
                      >
                        <Image className="h-4 w-4 mr-2" />
                        Gallery
                      </TabsTrigger>
                    </TabsList>
                    
                    {/* Quick Gallery Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActiveContentTab('gallery')}
                      className="hidden sm:flex items-center gap-2"
                      style={{ borderColor: '#D86B47', color: '#D86B47' }}
                      data-testid="view-gallery-btn"
                    >
                      <FileImage className="h-4 w-4" />
                      View Gallery
                    </Button>
                  </div>

                  {/* Course Content Tab */}
                  <TabsContent value="content" className="mt-0">
                    <div>
                      {lessons.length === 0 ? (
                        <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
                          <BookOpen className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                          <p style={{ color: '#5C635A' }}>No lessons available yet</p>
                        </Card>
                      ) : (
                        <Accordion type="single" collapsible className="space-y-2">
                          {lessons.map((lesson, index) => (
                            <AccordionItem
                              key={lesson.id}
                              value={lesson.id}
                              className="border rounded-lg px-4"
                              style={{ borderColor: '#E3E1DB' }}
                              data-testid={`lesson-${lesson.id}`}
                            >
                              <AccordionTrigger className="hover:no-underline">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium"
                                    style={{ backgroundColor: '#F0EFEA', color: '#163329' }}
                                  >
                                    {index + 1}
                                  </div>
                                  <div className="text-left">
                                    <div className="flex items-center gap-2">
                                      <h3 className="font-semibold" style={{ color: '#163329' }}>{lesson.title}</h3>
                                      {/* Content type badge - show even for locked lessons */}
                                      {lesson.locked ? (
                                        // Locked lesson - show content type if available, plus lock indicator
                                        <>
                                          {lesson.content_type === 'pdf' ? (
                                            <Badge className="text-xs" style={{ backgroundColor: '#D86B47', color: 'white' }}>
                                              <FileText className="h-3 w-3 mr-1" /> PDF
                                            </Badge>
                                          ) : lesson.content_type === 'youtube' ? (
                                            <Badge className="text-xs" style={{ backgroundColor: '#FF0000', color: 'white' }}>
                                              <Video className="h-3 w-3 mr-1" /> YouTube
                                            </Badge>
                                          ) : lesson.content_type === 'video' ? (
                                            <Badge className="text-xs" style={{ backgroundColor: '#163329', color: 'white' }}>
                                              <Video className="h-3 w-3 mr-1" /> Video
                                            </Badge>
                                          ) : null}
                                          <Badge variant="outline" className="text-xs" style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
                                            <Lock className="h-3 w-3 mr-1" /> Locked
                                          </Badge>
                                        </>
                                      ) : lesson.video_url ? (
                                        // Unlocked lesson with content
                                        lesson.video_url.endsWith('.pdf') ? (
                                          <Badge className="text-xs" style={{ backgroundColor: '#D86B47', color: 'white' }}>
                                            <FileText className="h-3 w-3 mr-1" /> PDF
                                          </Badge>
                                        ) : lesson.video_type === 'youtube' ? (
                                          <Badge className="text-xs" style={{ backgroundColor: '#FF0000', color: 'white' }}>
                                            <Video className="h-3 w-3 mr-1" /> YouTube
                                          </Badge>
                                        ) : (
                                          <Badge className="text-xs" style={{ backgroundColor: '#163329', color: 'white' }}>
                                            <Video className="h-3 w-3 mr-1" /> Video
                                          </Badge>
                                        )
                                      ) : (
                                        // Genuinely no content
                                        <Badge variant="outline" className="text-xs text-gray-500">
                                          No content yet
                                        </Badge>
                                      )}
                                      {/* Purchasable lesson indicator */}
                                      {lesson.is_purchasable && lesson.price > 0 && !is_enrolled && (
                                        <Badge className="text-xs" style={{ backgroundColor: '#0070ba', color: 'white' }}>
                                          <Usd value={lesson.price} />
                                        </Badge>
                                      )}
                                      {lesson.is_purchasable && lesson.price > 0 && !is_enrolled && <PriceCaption className="w-full" />}
                                    </div>
                                    {lesson.duration && (
                                      <p className="text-sm" style={{ color: '#5C635A' }}>{lesson.duration} min</p>
                                    )}
                                  </div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <p className="mt-2" style={{ color: '#5C635A' }}>{lesson.description}</p>
                                {is_enrolled && !is_expired && (
                                  <div className="mt-3 space-y-3">
                                    <Button
                                      size="sm"
                                      className="rounded-full"
                                      style={{ backgroundColor: '#D86B47', color: '#FFFFFF' }}
                                      onClick={() => navigate(`/learn/${courseId}/lesson/${lesson.id}`)}
                                      data-testid={`watch-lesson-${lesson.id}`}
                                    >
                                      {lesson.video_url ? (
                                        lesson.video_url.endsWith('.pdf') ? (
                                          <>
                                            <FileText className="h-4 w-4 mr-2" />
                                            View PDF
                                          </>
                                        ) : (
                                          <>
                                            <Play className="h-4 w-4 mr-2" />
                                            Watch Lesson
                                          </>
                                        )
                                      ) : (
                                        <>
                                          <Play className="h-4 w-4 mr-2" />
                                          View Lesson
                                        </>
                                      )}
                                    </Button>
                                    
                                    {/* Quizzes for this lesson */}
                                    {lessonQuizzes[lesson.id]?.length > 0 && (
                                      <div className="pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
                                        <p className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: '#163329' }}>
                                          <ClipboardList className="h-4 w-4" />
                                          Quizzes
                                        </p>
                                        <div className="space-y-2">
                                          {lessonQuizzes[lesson.id].map((quiz) => (
                                            <div 
                                              key={quiz.id}
                                              className="flex items-center justify-between p-2 rounded-lg"
                                              style={{ backgroundColor: '#F0EFEA' }}
                                            >
                                              <div className="flex items-center gap-2">
                                                <ClipboardList className="h-4 w-4" style={{ color: '#163329' }} />
                                                <span className="text-sm font-medium" style={{ color: '#163329' }}>{quiz.title}</span>
                                                <Badge variant="outline" className="text-xs">
                                                  {quiz.questions?.length || 0} questions
                                                </Badge>
                                              </div>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openQuiz(quiz.id)}
                                                className="text-xs"
                                              >
                                                Take Quiz
                                              </Button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {is_enrolled && is_expired && (
                                  <div 
                                    className="mt-3 p-3 rounded-lg flex items-center gap-2"
                                    style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                                  >
                                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                    <span className="text-sm">Course access expired. Contact admin to extend.</span>
                                  </div>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      )}
                    </div>
                  </TabsContent>

                  {/* Brochure Tab */}
                  <TabsContent value="brochure" className="mt-0">
                    <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                      {(course.brochure_text || (course.brochure_files && course.brochure_files.length > 0)) ? (
                        <div className="space-y-6">
                          {/* Brochure Text */}
                          {course.brochure_text && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5" style={{ color: '#D86B47' }} />
                                <h3 className="font-semibold" style={{ color: '#163329' }}>About This Course</h3>
                              </div>
                              <div 
                                className="prose prose-sm max-w-none p-4 rounded-lg"
                                style={{ backgroundColor: '#F5F5F0', color: '#333' }}
                              >
                                {course.brochure_text.split('\n').map((paragraph, idx) => (
                                  <p key={idx} className="mb-2 last:mb-0">{paragraph}</p>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Brochure PDF Files */}
                          {course.brochure_files && course.brochure_files.length > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5" style={{ color: '#D86B47' }} />
                                <h3 className="font-semibold" style={{ color: '#163329' }}>
                                  Course Brochures ({course.brochure_files.length} {course.brochure_files.length === 1 ? 'file' : 'files'})
                                </h3>
                              </div>
                              <BrochureFileList courseId={course.id} files={course.brochure_files} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                          <p style={{ color: '#5C635A' }}>No brochure available for this course</p>
                        </div>
                      )}
                    </Card>
                  </TabsContent>

                  {/* Gallery Tab */}
                  <TabsContent value="gallery" className="mt-0">
                    <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                      {galleryImages.length > 0 ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 mb-4">
                            <Image className="h-6 w-6" style={{ color: '#D86B47' }} />
                            <h3 className="font-semibold" style={{ color: '#163329' }}>
                              Course Gallery ({galleryImages.length} images)
                            </h3>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {galleryImages.map((image, index) => (
                              <div 
                                key={image.id || index}
                                className="relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity group"
                                onClick={() => window.open(image.presigned_url || image.url, '_blank')}
                              >
                                <img
                                  src={image.presigned_url || image.url}
                                  alt={image.title || `Gallery image ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                {(image.title || image.description) && (
                                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {image.title && (
                                      <p className="text-white text-sm font-medium truncate">{image.title}</p>
                                    )}
                                    {image.description && (
                                      <p className="text-white text-xs truncate">{image.description}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Image className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                          <p style={{ color: '#5C635A' }}>No gallery images available for this course</p>
                        </div>
                      )}
                    </Card>
                  </TabsContent>
                </Tabs>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="sticky top-24"
              >
                <Card style={{ borderColor: '#E3E1DB' }} data-testid="enrollment-card">
                  <CardHeader>
                    <div className="mb-2">
                      <span className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }} data-testid="course-detail-price">
                        {course.price === 0 ? 'Free' : formatPrice(course.price)}
                      </span>
                      {course.price !== 0 && <PriceCaption />}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {is_enrolled ? (
                      <>
                        {is_expired ? (
                          // Expired enrollment
                          <div className="space-y-3">
                            <div 
                              className="flex items-center gap-2 p-3 rounded-lg"
                              style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                              data-testid="course-expired-notice"
                            >
                              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                              <div>
                                <p className="font-medium">Course Access Expired</p>
                                <p className="text-sm">Contact admin to extend your enrollment.</p>
                              </div>
                            </div>
                            <Button
                              className="w-full"
                              variant="outline"
                              onClick={() => navigate('/student')}
                              data-testid="back-to-dashboard-button"
                            >
                              Back to Dashboard
                            </Button>
                          </div>
                        ) : (
                          // Active enrollment
                          <>
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="h-5 w-5" />
                              <span className="font-medium">You're enrolled!</span>
                            </div>
                            {days_remaining !== null && (
                              <div 
                                className="flex items-center gap-2 p-2 rounded-lg text-sm"
                                style={{ 
                                  backgroundColor: days_remaining <= 7 ? '#FEF3C7' : '#F0EFEA',
                                  color: days_remaining <= 7 ? '#D97706' : '#5C635A'
                                }}
                                data-testid="course-validity-info"
                              >
                                <Clock className="h-4 w-4 flex-shrink-0" />
                                <span>
                                  {days_remaining <= 0 
                                    ? 'Access expires today' 
                                    : days_remaining === 1 
                                      ? '1 day of access remaining' 
                                      : `${days_remaining} days of access remaining`}
                                </span>
                              </div>
                            )}
                            <Button
                              className="w-full"
                              
                              onClick={() => navigate(`/learn/${courseId}/lesson/${lessons[0]?.id}`)}
                              data-testid="continue-learning-button"
                              disabled={!lessons || lessons.length === 0}
                            >
                              Continue Learning
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Warning if paid course has no declaration */}
                        {course.price > 0 && !courseDeclaration && (
                          <div className="mb-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}>
                            <p className="font-medium text-amber-700">Declaration Required</p>
                            <p className="text-amber-600 text-xs mt-1">
                              This course requires an approved Acknowledgment Declaration before purchase. Please contact the instructor.
                            </p>
                          </div>
                        )}
                        <Button
                          className="w-full"
                          
                          onClick={handleEnroll}
                          disabled={isEnrolling || isPurchasing || (course.price > 0 && !courseDeclaration)}
                          data-testid="enroll-button"
                        >
                          {isPurchasing ? 'Processing...' : isEnrolling ? 'Enrolling...' : course.price === 0 ? 'Enroll Now' : 'Buy Now'}
                        </Button>
                      </>
                    )}
                    <Separator />
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2" style={{ color: '#5C635A' }}>
                        <Video className="h-4 w-4" />
                        <span>{lessons.length} video lessons</span>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: '#5C635A' }}>
                        <BookOpen className="h-4 w-4" />
                        <span>Lifetime access</span>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: '#5C635A' }}>
                        <Calendar className="h-4 w-4" />
                        <span>Learn at your own pace</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Your Instructor - Full Width Section at Bottom */}
      {teacher && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Your Instructor
            </h2>
            <Card style={{ borderColor: '#E3E1DB' }}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-bold overflow-hidden flex-shrink-0 mx-auto sm:mx-0"
                    style={{ backgroundColor: '#E8E6E1', color: '#163329' }}
                  >
                    {teacher.avatar_url ? (
                      <img 
                        src={teacher.avatar_url.startsWith('/api/') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${teacher.avatar_url}` 
                          : teacher.avatar_url} 
                        alt={teacher.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      teacher.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 text-center sm:text-left min-w-0">
                    <h3 className="text-xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      {teacher.name}
                    </h3>
                    <p className="text-sm break-all" style={{ color: '#5C635A' }}>{teacher.email}</p>
                    {teacher.phone && (
                      <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                        <span className="font-medium">Phone:</span> {teacher.phone}
                      </p>
                    )}
                    {teacher.bio && (
                      <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: '#5C635A' }}>
                        {teacher.bio}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Payment Options Dialog - PayPal Only */}
      <Dialog open={showPaymentOptions} onOpenChange={setShowPaymentOptions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Complete Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm" style={{ color: '#5C635A' }}>
              Pay for <strong>{courseData?.course?.title}</strong>
            </p>
            <p className="text-lg font-bold" style={{ color: '#163329' }}>
              {formatPrice(courseData?.course?.price || 0)}
            </p>
            <PriceCaption />
            
            <div className="space-y-3">
              {/* PayPal Option */}
              <Button
                onClick={handlePayPalPayment}
                disabled={isPurchasing}
                className="w-full justify-start h-14 rounded-lg"
                variant="outline"
                style={{ borderColor: '#E3E1DB' }}
              >
                {isPurchasing ? (
                  <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                ) : (
                  <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24" fill="#003087">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.83A.78.78 0 0 1 5.715 2.2h7.058c2.288 0 4.148.735 5.077 2.004.851 1.163 1.066 2.718.6 4.472-.014.057-.032.113-.048.17a7.93 7.93 0 0 1-.307.908c-.967 2.436-3.15 3.646-6.084 3.646h-1.59a.77.77 0 0 0-.76.652l-.82 5.127a.768.768 0 0 1-.765.658z"/>
                  </svg>
                )}
                <div className="text-left">
                  <p className="font-medium" style={{ color: '#163329' }}>Pay with PayPal</p>
                  <p className="text-xs" style={{ color: '#5C635A' }}>Secure payment via PayPal</p>
                </div>
              </Button>
            </div>
            
            <p className="text-xs text-center" style={{ color: '#5C635A' }}>
              Your payment is secure and encrypted
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quiz Taking Dialog */}
      <Dialog open={!!activeQuiz} onOpenChange={(open) => !open && closeQuiz()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              {activeQuiz?.title}
            </DialogTitle>
          </DialogHeader>
          
          {quizResult ? (
            // Show results
            <div className="space-y-6 pt-4">
              <div className="text-center py-6">
                <div 
                  className="w-24 h-24 rounded-full mx-auto flex items-center justify-center mb-4"
                  style={{ 
                    backgroundColor: quizResult.score >= 70 ? '#E8F5E9' : '#FFF8E7',
                    color: quizResult.score >= 70 ? '#2E7D32' : '#E2A761'
                  }}
                >
                  <Award className="h-12 w-12" />
                </div>
                <h3 className="text-3xl font-bold mb-2" style={{ color: '#163329' }}>
                  {quizResult.score}%
                </h3>
                <p style={{ color: '#5C635A' }}>
                  You got {quizResult.correct_count} out of {quizResult.total_questions} questions correct
                </p>
                {quizResult.score >= 70 ? (
                  <p className="text-green-600 font-medium mt-2">Great job! You passed!</p>
                ) : (
                  <p className="text-orange-600 font-medium mt-2">Keep practicing! You can retake this quiz.</p>
                )}
              </div>
              
              <div className="space-y-3">
                {activeQuiz?.questions?.map((q, qIndex) => (
                  <div 
                    key={qIndex}
                    className="p-4 rounded-lg"
                    style={{ 
                      backgroundColor: quizAnswers[qIndex] === q.correct_answer ? '#E8F5E9' : '#FFEBEE',
                      borderLeft: `4px solid ${quizAnswers[qIndex] === q.correct_answer ? '#2E7D32' : '#C62828'}`
                    }}
                  >
                    <div className="flex items-start gap-2">
                      {quizAnswers[qIndex] === q.correct_answer ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium" style={{ color: '#163329' }}>{q.question}</p>
                        <p className="text-sm mt-1">
                          <span style={{ color: '#5C635A' }}>Your answer: </span>
                          <span className={quizAnswers[qIndex] === q.correct_answer ? 'text-green-700' : 'text-red-700'}>
                            {q.options[quizAnswers[qIndex]]}
                          </span>
                        </p>
                        {quizAnswers[qIndex] !== q.correct_answer && (
                          <p className="text-sm text-green-700">
                            Correct answer: {q.options[q.correct_answer]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={closeQuiz}
                className="w-full"
                
              >
                Close
              </Button>
            </div>
          ) : (
            // Show quiz questions
            <div className="space-y-6 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  {Object.keys(quizAnswers).length} of {activeQuiz?.questions?.length || 0} answered
                </p>
                <Badge variant="outline">
                  {activeQuiz?.questions?.length || 0} questions
                </Badge>
              </div>

              <div className="space-y-6">
                {activeQuiz?.questions?.map((q, qIndex) => (
                  <div key={qIndex} className="space-y-3">
                    <p className="font-medium" style={{ color: '#163329' }}>
                      {qIndex + 1}. {q.question}
                    </p>
                    <div className="space-y-2">
                      {q.options?.map((option, oIndex) => (
                        <div
                          key={oIndex}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            quizAnswers[qIndex] === oIndex 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleQuizAnswer(qIndex, oIndex)}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                quizAnswers[qIndex] === oIndex 
                                  ? 'border-green-500 bg-green-500' 
                                  : 'border-gray-300'
                              }`}
                            >
                              {quizAnswers[qIndex] === oIndex && (
                                <CheckCircle className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <span style={{ color: '#163329' }}>{option}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleSubmitQuiz}
                disabled={isSubmittingQuiz || Object.keys(quizAnswers).length < (activeQuiz?.questions?.length || 0)}
                className="w-full"
                
              >
                {isSubmittingQuiz ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Quiz'
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Course Terms Acceptance Modal */}
      <Dialog open={showTermsModal} onOpenChange={() => {}}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] overflow-hidden"
          style={{ backgroundColor: '#FFFFFF' }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#163329' }}>
              <FileText className="inline-block mr-2 h-5 w-5" />
              Course Terms & Conditions
            </DialogTitle>
            <DialogDescription style={{ color: '#5C635A' }}>
              {courseTerms?.message || `Please review and accept the terms for "${courseTerms?.course_title}"`}
            </DialogDescription>
          </DialogHeader>
          
          <div 
            className="overflow-y-auto max-h-[50vh] p-4 rounded-lg my-4"
            style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}
          >
            <div 
              className="prose prose-sm max-w-none"
              style={{ color: '#333' }}
            >
              {courseTerms?.terms_and_conditions?.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3">{paragraph}</p>
              ))}
            </div>
          </div>
          
          {courseTerms?.terms_updated_at && (
            <p className="text-xs mb-4" style={{ color: '#5C635A' }}>
              Last updated: {new Date(courseTerms.terms_updated_at).toLocaleDateString()}
            </p>
          )}
          
          <div className="flex items-center space-x-3 mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FFF3E0', border: '1px solid #FFE0B2' }}>
            <Checkbox
              id="accept-course-terms"
              checked={termsAccepted}
              onCheckedChange={setTermsAccepted}
              data-testid="course-terms-checkbox"
            />
            <label 
              htmlFor="accept-course-terms" 
              className="text-sm cursor-pointer"
              style={{ color: '#E65100' }}
            >
              I have read and agree to the course terms and conditions
            </label>
          </div>
          
          <Button
            onClick={handleAcceptTerms}
            disabled={!termsAccepted || isAcceptingTerms}
            className="w-full"
            style={{ backgroundColor: '#163329' }}
            data-testid="accept-course-terms-button"
          >
            {isAcceptingTerms ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Accepting...
              </>
            ) : (
              'Accept & Continue to Lessons'
            )}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Course Declaration Acceptance Modal */}
      <Dialog open={showDeclarationModal} onOpenChange={setShowDeclarationModal}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] overflow-hidden"
          style={{ backgroundColor: '#FFFFFF' }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#163329' }}>
              <FileText className="inline-block mr-2 h-5 w-5" />
              Acknowledgment Declaration
            </DialogTitle>
            <DialogDescription style={{ color: '#5C635A' }}>
              Please read and acknowledge the following declaration before enrolling in "{courseData?.course?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div 
            className="overflow-y-auto max-h-[50vh] p-4 rounded-lg my-4"
            style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}
          >
            <div 
              className="prose prose-sm max-w-none whitespace-pre-wrap"
              style={{ color: '#333' }}
            >
              {courseDeclaration?.text?.replace(
                /\[Full Name\]/g, 
                (() => {
                  const user = getUser();
                  if (user) {
                    const fullName = user.surname 
                      ? `${user.name} ${user.surname}`
                      : user.name;
                    return fullName;
                  }
                  return '[Your Name]';
                })()
              )}
            </div>
          </div>
          
          <div className="flex items-start space-x-3 mb-4 p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}>
            <Checkbox
              id="accept-declaration"
              checked={declarationAccepted}
              onCheckedChange={setDeclarationAccepted}
              className="mt-0.5"
              data-testid="declaration-checkbox"
            />
            <label 
              htmlFor="accept-declaration" 
              className="text-sm cursor-pointer"
              style={{ color: '#2E7D32' }}
            >
              I, <strong>{(() => {
                const user = getUser();
                if (user) {
                  return user.surname ? `${user.name} ${user.surname}` : user.name;
                }
                return 'Learner';
              })()}</strong>, have read, understood, and agree to the above declaration
            </label>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeclarationModal(false);
                setDeclarationAccepted(false);
              }}
              className="flex-1"
              data-testid="cancel-declaration-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAcceptDeclaration}
              disabled={!declarationAccepted || isAcceptingDeclaration}
              className="flex-1"
              style={{ backgroundColor: '#163329' }}
              data-testid="accept-declaration-button"
            >
              {isAcceptingDeclaration ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Accept & Continue'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourseDetailPage;